import { InjectionToken } from '@angular/core';

import { type ErrorReportUploadProvider, type LegacyEndpointConfig, type ProductKey } from './interfaces';

/** Product key sent on the proxy POST. Each consumer binds its own value. */
export const ERROR_REPORT_PRODUCT_KEY = new InjectionToken<ProductKey>('ERROR_REPORT_PRODUCT_KEY');

/** Proxy host(s) for `POST /api/v1/error-report`. Typically `environment.endpoints.speechRecognitionProxy`. */
export const ERROR_REPORT_PROXY_ENDPOINT = new InjectionToken<string[]>('ERROR_REPORT_PROXY_ENDPOINT');

/**
 * Optional dual-write target for products that still need to ping a legacy backend
 * (e.g. AI Tutor → `ai-tutor-api`). Bind `null` for proxy-only products.
 *
 * Prefer {@link ERROR_REPORT_LEGACY_CONFIG_BY_PRODUCT} for app-level wiring —
 * this single-config token stays for back-compat with surfaces that scope an
 * `ErrorReportApiService` instance to one product.
 */
export const ERROR_REPORT_LEGACY_CONFIG = new InjectionToken<LegacyEndpointConfig | null>('ERROR_REPORT_LEGACY_CONFIG');

/**
 * Per-product legacy dual-write map. When bound, the lib resolves the legacy
 * endpoint per submit by `options.product`, taking precedence over the
 * single-config token above. Lets one root-scoped service serve multiple
 * surfaces (math-trainer / student-cabinet / ai-tutor) AND honour the
 * curator-cabinet selector — when a curator picks `math-trainer`, the
 * math entry fires `/v1/code/feedback` exactly as a native math submit would.
 * Products absent from the map fall through to proxy-only.
 */
export const ERROR_REPORT_LEGACY_CONFIG_BY_PRODUCT = new InjectionToken<
  Partial<Record<ProductKey, LegacyEndpointConfig>>
>('ERROR_REPORT_LEGACY_CONFIG_BY_PRODUCT');

/** Upload bridge — each consumer provides its product-specific upload service. */
export const ERROR_REPORT_UPLOAD_PROVIDER = new InjectionToken<ErrorReportUploadProvider>(
  'ERROR_REPORT_UPLOAD_PROVIDER',
);

/**
 * Kill-switch evaluated on every submit. When the resolver returns `true`,
 * the lib service skips the proxy POST and falls back to legacy-only. Bind
 * `null` (or omit) when no kill switch is wired.
 */
export const ERROR_REPORT_DUAL_WRITE_DISABLED = new InjectionToken<() => boolean>('ERROR_REPORT_DUAL_WRITE_DISABLED');

/**
 * Optional app version string surfaced as `clientMeta.appVersion`. Bind to
 * `environment.app?.commitHash ?? null`. Omitted from payload when not bound.
 */
export const ERROR_REPORT_APP_VERSION = new InjectionToken<string | null>('ERROR_REPORT_APP_VERSION');
